import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Form, Dropdown } from 'semantic-ui-react'
import { useApiQuery } from '../../../../../hooks/useApiQuery.js'

/**
 * CompanySelectFilter
 *
 * Searchable company select filter.
 * Used for IMC and Carrier filters.
 */
export const CompanySelectFilter = ({
  value,
  onChange,
  companyType,
  multiple,
  placeholder,
  label,
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOptions, setSelectedOptions] = useState([])

  // Fetch companies based on search
  const { data: companiesResponse, isLoading } = useApiQuery({
    key: ['searchCompanies', companyType, searchQuery],
    payload: {
      query: searchQuery,
      company_type: companyType,
      is_active: null,
      ignore_active_filter: true,
      page_size: 50,
    },
    enabled: searchQuery.length >= 1,
    staleTime: 30000,
  })

  const companies = companiesResponse?.results || []

  // Merge search results with previously selected options to persist them
  const mergedOptions = useMemo(() => {
    const searchOptions = companies.map((company) => ({
      key: company.uid,
      value: company.uid,
      text: company.name,
      data: company,
    }))
    const searchUids = new Set(searchOptions.map((o) => o.value))
    const preserved = selectedOptions.filter((o) => !searchUids.has(o.value))
    return [...preserved, ...searchOptions]
  }, [companies, selectedOptions])

  // Handle search change
  const handleSearchChange = useCallback((e, { searchQuery: query }) => {
    setSearchQuery(query)
  }, [])

  // Handle selection change
  //
  // Selections are resolved against `mergedOptions` (current results + the
  // options we have preserved across searches), never against `companies`
  // (the current result set only). This keeps a previously-selected company
  // resolvable after a new search drops it from the results: in single-select
  // it returns the full object instead of clearing to null, and in multi-select
  // it keeps the full object instead of degrading to a bare { uid }.
  const handleChange = useCallback(
    (e, { value: selectedValue }) => {
      if (multiple) {
        const newSelectedOpts = selectedValue.map((uid) => {
          const existing = mergedOptions.find((o) => o.value === uid)
          return existing || { key: uid, value: uid, text: uid, data: { uid } }
        })
        setSelectedOptions(newSelectedOpts)
        onChange(newSelectedOpts.map((o) => o.data))
      } else {
        const existing = mergedOptions.find((o) => o.value === selectedValue)
        if (existing) {
          setSelectedOptions([existing])
        }
        onChange(existing ? existing.data : null)
      }
    },
    [mergedOptions, multiple, onChange],
  )

  // Get current value for dropdown
  const dropdownValue = multiple
    ? (value || []).map((v) => v.uid || v)
    : value?.uid || value || ''

  return (
    <Form.Field>
      <label>{label}</label>
      <Dropdown
        placeholder={placeholder}
        fluid
        selection
        search
        multiple={multiple}
        options={mergedOptions}
        value={dropdownValue}
        onChange={handleChange}
        onSearchChange={handleSearchChange}
        loading={isLoading}
        noResultsMessage={
          searchQuery.length < 2 ? 'Type to search...' : 'No results found'
        }
      />
    </Form.Field>
  )
}

CompanySelectFilter.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  onChange: PropTypes.func.isRequired,
  companyType: PropTypes.oneOf(['imc', 'carrier']).isRequired,
  multiple: PropTypes.bool,
  placeholder: PropTypes.string,
  label: PropTypes.string,
}

CompanySelectFilter.defaultProps = {
  value: null,
  multiple: false,
  placeholder: 'Search companies...',
  label: 'Company',
}
